import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { parsePhoneNumber, isValidPhoneNumber, CountryCode, getCountryCallingCode } from 'libphonenumber-js';
import { cn } from '@/lib/utils';

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  id?: string;
  required?: boolean;
}

const countries = [
  { code: 'US', name: 'United States', flag: '🇺🇸' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦' },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'AU', name: 'Australia', flag: '🇦🇺' },
  { code: 'DE', name: 'Germany', flag: '🇩🇪' },
  { code: 'FR', name: 'France', flag: '🇫🇷' },
  { code: 'IT', name: 'Italy', flag: '🇮🇹' },
  { code: 'ES', name: 'Spain', flag: '🇪🇸' },
  { code: 'MX', name: 'Mexico', flag: '🇲🇽' },
  { code: 'BR', name: 'Brazil', flag: '🇧🇷' },
  { code: 'IN', name: 'India', flag: '🇮🇳' },
  { code: 'JP', name: 'Japan', flag: '🇯🇵' },
  { code: 'KR', name: 'South Korea', flag: '🇰🇷' },
  { code: 'CN', name: 'China', flag: '🇨🇳' },
] as const;

export const PhoneInput: React.FC<PhoneInputProps> = ({
  value,
  onChange,
  placeholder,
  className,
  id,
  required = false,
}) => {
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>('US');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isValid, setIsValid] = useState(true);

  // Get country calling code
  const countryCode = getCountryCallingCode(selectedCountry);

  // Update placeholder based on selected country
  const getPlaceholder = () => {
    switch (selectedCountry) {
      case 'US':
      case 'CA':
        return '+1 555 123 4567';
      case 'GB':
        return '+44 20 7946 0958';
      case 'AU':
        return '+61 2 9374 4000';
      case 'DE':
        return '+49 30 12345678';
      case 'FR':
        return '+33 1 42 86 83 26';
      default:
        return `+${countryCode} ...`;
    }
  };

  const formatAndValidatePhone = (inputValue: string, country: CountryCode) => {
    // Remove any non-digit characters except +
    const cleaned = inputValue.replace(/[^\d+]/g, '');
    
    try {
      // If the input doesn't start with +, prepend the country code
      let fullNumber = cleaned;
      if (!cleaned.startsWith('+')) {
        fullNumber = `+${countryCode}${cleaned}`;
      }

      // Parse and validate the phone number
      const parsed = parsePhoneNumber(fullNumber);
      if (parsed && parsed.isValid()) {
        setIsValid(true);
        return parsed.format('E.164');
      } else {
        // Check if it's a valid phone number for the selected country
        const isValidForCountry = isValidPhoneNumber(cleaned, country);
        setIsValid(isValidForCountry);
        return fullNumber;
      }
    } catch (error) {
      setIsValid(false);
      return cleaned.startsWith('+') ? cleaned : `+${countryCode}${cleaned}`;
    }
  };

  const handlePhoneChange = (inputValue: string) => {
    setPhoneNumber(inputValue);
    const formattedNumber = formatAndValidatePhone(inputValue, selectedCountry);
    onChange(formattedNumber);
  };

  const handleCountryChange = (newCountry: string) => {
    const country = newCountry as CountryCode;
    setSelectedCountry(country);
    
    // Re-format the current phone number with the new country
    if (phoneNumber) {
      const formattedNumber = formatAndValidatePhone(phoneNumber, country);
      onChange(formattedNumber);
    }
  };

  // Initialize phone number display from value prop
  useEffect(() => {
    if (value && value !== phoneNumber) {
      try {
        const parsed = parsePhoneNumber(value);
        if (parsed) {
          setSelectedCountry(parsed.country || 'US');
          setPhoneNumber(parsed.nationalNumber);
          setIsValid(parsed.isValid());
        }
      } catch {
        setPhoneNumber(value.replace(/^\+\d+/, ''));
      }
    }
  }, [value]);

  return (
    <div className="flex gap-2">
      <Select value={selectedCountry} onValueChange={handleCountryChange}>
        <SelectTrigger className="w-[140px] h-11">
          <SelectValue>
            <div className="flex items-center gap-2">
              <span>{countries.find(c => c.code === selectedCountry)?.flag}</span>
              <span className="text-sm">+{countryCode}</span>
            </div>
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {countries.map((country) => (
            <SelectItem key={country.code} value={country.code}>
              <div className="flex items-center gap-2">
                <span>{country.flag}</span>
                <span className="text-sm">+{getCountryCallingCode(country.code as CountryCode)}</span>
                <span className="text-sm text-muted-foreground">{country.name}</span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      
      <Input
        id={id}
        type="tel"
        placeholder={placeholder || getPlaceholder()}
        value={phoneNumber}
        onChange={(e) => handlePhoneChange(e.target.value)}
        required={required}
        className={cn(
          "flex-1 h-11",
          !isValid && "border-destructive focus-visible:ring-destructive",
          className
        )}
      />
    </div>
  );
};